// Story 13.x — Dialog para guardar el bundle del bot actual como template
// custom del usuario. Lee el árbol de archivos del bot, descarga el
// contenido de cada uno (paralelo) y POSTea a `/api/v1/bots/templates`.
//
// Bloquea si hay tabs dirty: la copia que se guardaría sería el último
// estado persistido, no lo que el user ve. Mostramos un CTA inline para
// que primero guarde.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { type BotRowSummary, botsApi, filesApi } from '@/lib/api-client';
import { isDirty } from '@/lib/editor-tabs-reducer';
import { flattenFiles } from '@/lib/editor-types';
import { useEditorStore } from '@/stores/editor-store';
import { Modal } from './Modal';

interface Props {
  open: boolean;
  onClose: () => void;
  bot: BotRowSummary | undefined;
}

const NAME_PATTERN = /^[a-z0-9][a-z0-9_-]*$/;

function deriveDefaultName(botName: string): string {
  return botName
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

export function SaveAsTemplateDialog({ open, onClose, bot }: Props): React.ReactElement {
  const tabs = useEditorStore((s) => s.tabs);
  const dirtyCount = tabs.filter(isDirty).length;
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && bot) {
      setName(deriveDefaultName(bot.name));
      setDescription(`Plantilla derivada de ${bot.name}`);
      setError(null);
    }
  }, [open, bot]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!bot) throw new Error('No bot context');
      // 1. Listar el árbol — dependencias mínimas, sólo necesitamos paths.
      const tree = await filesApi.tree(bot.id);
      const paths = flattenFiles(tree).map((f) => f.path);
      if (paths.length === 0) {
        throw new Error('El bot no tiene archivos para guardar como template.');
      }
      // 2. Bajar el contenido en paralelo. El backend cachea via Drizzle,
      //    no nos preocupa thundering herd contra ~10 archivos.
      const contents = await Promise.all(paths.map((p) => filesApi.content(bot.id, p)));
      const files: Record<string, string> = {};
      for (let i = 0; i < paths.length; i++) {
        files[paths[i]] = contents[i].content;
      }
      // 3. POSTear el bundle. El backend valida shape + presencia de bot.yaml.
      return botsApi.saveTemplate({ name, description, files });
    },
    onSuccess: () => {
      toast.success(`✓ Template "${name}" guardado.`);
      // Invalidamos la lista de templates para que el wizard la refresque
      // la próxima vez que abra.
      void qc.invalidateQueries({ queryKey: ['bot-templates'] });
      onClose();
    },
    onError: (err) => {
      setError((err as Error).message);
    },
  });

  function handleSubmit(): void {
    setError(null);
    if (!NAME_PATTERN.test(name)) {
      setError(
        'Nombre inválido. Usa sólo letras minúsculas, dígitos, hyphens o underscores; debe empezar con letra/dígito.',
      );
      return;
    }
    if (description.length === 0) {
      setError('La descripción no puede estar vacía.');
      return;
    }
    if (dirtyCount > 0) {
      setError(
        `Tienes ${dirtyCount} archivo${dirtyCount === 1 ? '' : 's'} sin guardar. Guarda primero para que el template refleje el estado real.`,
      );
      return;
    }
    mutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Guardar como template"
      description={
        bot
          ? `Crea una plantilla reutilizable a partir del bot "${bot.name}". Va a aparecer en el wizard cuando crees nuevos bots.`
          : undefined
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!bot || mutation.isPending}>
            {mutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Guardar template
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <Label htmlFor="tpl-name" className="text-xs">
            Nombre (slug)
          </Label>
          <Input
            id="tpl-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="mi-bot-base"
          />
          <p className="mt-1 text-[10px] text-muted-foreground">
            Lowercase, dígitos, hyphens o underscores. Si ya tienes uno con el mismo nombre se
            sobrescribe.
          </p>
        </div>
        <div>
          <Label htmlFor="tpl-desc" className="text-xs">
            Descripción
          </Label>
          <textarea
            id="tpl-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1 text-sm"
            placeholder="¿Qué hace este template?"
          />
        </div>
        {dirtyCount > 0 && (
          <p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-500">
            Tenés {dirtyCount} archivo{dirtyCount === 1 ? '' : 's'} sin guardar. El template no
            incluiría esos cambios — guardá primero.
          </p>
        )}
        {error && (
          <p className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
